import { useManager } from 'dreamstate';
import { memo, useCallback, useState } from 'react';

import { Alert, rem } from '@vimeo/bokeh';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Id, Optional } from '@vimeo/core/types/generic';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import {
  ESidebarAttach,
  ResponsiveSidebar,
  TConfigProp,
} from '@vimeo/live-core/components/sidebar';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

import { useInteractionSidebarItems } from './InteractionSidebar.items';

export interface IInteractionSidebarProps extends IBaseDomElementProps {
  attach: TConfigProp<ESidebarAttach>;
  isPortrait?: boolean;
  isFullscreen?: boolean;
  isStandalone?: boolean;
  maxSize?: TConfigProp<number>;
  interactionSessionContext?: IInteractionSessionContext;
  onOpen?: (active: Optional<Id>) => void;
  onClose?: (active: Optional<Id>) => void;
  onToggleExtend?: (isExtended: boolean) => void;
}

export const InteractionSidebar = memo(function ({
  id = createInteractionDomName('interaction-sidebar'),
  attach,
  isPortrait,
  isStandalone,
  isFullscreen,
  maxSize,
  onOpen,
  onClose,
  onToggleExtend,
  interactionSessionContext: {
    roomInfo: { value: roomInfo },
    roomUser: { value: roomUser },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}: IInteractionSidebarProps) {
  const {
    initialState,
    initialState: { interaction },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  const sidebarState = useState<Optional<Id>>(null);
  const [isModeratorNoticeVisible, setModeratorNoticeVisible] = useState<boolean>(
    Boolean(!roomUser?.isCreator && roomInfo?.settings.authOptions.admin),
  );

  const { items } = useInteractionSidebarItems(sidebarState[0]);

  const isManagementAccessed: boolean = Boolean(
    roomUser?.isCreator && !interaction?.feature?.isManagementDisabled,
  );

  const onCloseNotice = useCallback(
    () => setModeratorNoticeVisible(false),
    [setModeratorNoticeVisible],
  );

  return (
    <ResponsiveSidebar
      id={id}
      state={sidebarState}
      items={items}
      attach={attach}
      isPortrait={isPortrait}
      isFullscreen={isFullscreen}
      isInactiveDisabled={isStandalone}
      isWithHeader={isManagementAccessed || attach !== ESidebarAttach.TOP}
      maxSize={maxSize}
      contentNotice={
        isModeratorNoticeVisible ? (
          <Alert
            margin={`${rem(24)} ${rem(24)} 0`}
            width={'auto'}
            maxWidth={'100%'}
            fontSize={'text-sm'}
            onClose={onCloseNotice}
          >
            {translations.youDoNotHaveAccessToModerateThisEvent}
          </Alert>
        ) : null
      }
      backgroundColor={initialState?.interaction?.backgroundColor}
      onOpen={onOpen}
      onClose={onClose}
      onToggleExtend={onToggleExtend}
    />
  );
});
